import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ClinicManagerService } from '../../../networking/services/clinic-manager.service';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-change-password-dialog',
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatIconModule],
  templateUrl: './change-password-dialog.component.html',
  styleUrl: './change-password-dialog.component.scss'
})
export class ChangePasswordDialogComponent implements OnInit {
  form!: FormGroup
  hideOld = true
  hideNew = true
  hideConfirm = true
  isLoading = false
  errorMessage: string | null = null
  successMessage: string | null = null
  private isClosing = false

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    private clinicManagerService: ClinicManagerService
  ) {}

  ngOnInit(): void {
    this.dialogRef.backdropClick().subscribe(() => this.tryClose())
    this.dialogRef.keydownEvents().subscribe(e => {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.tryClose()
      }
    })

    this.form = this.fb.group({
      old_password: ['', [Validators.required]],
      new_password: ['', [Validators.required, Validators.minLength(8)]],
      confirm_password: ['', [Validators.required]]
    })
  }

  get passwordMismatch(): boolean {
    const newPw = this.form.get('new_password')?.value
    const confirmPw = this.form.get('confirm_password')?.value
    return newPw && confirmPw && newPw !== confirmPw
  }

  async tryClose(): Promise<void> {
    if (this.isClosing) return
    if (this.form.dirty && !this.successMessage) {
      this.isClosing = true
      try {
        const ref = this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: 'Änderungen verwerfen',
            message: 'Du hast ungespeicherte Änderungen. Wenn du den Dialog verlässt, gehen diese verloren.',
            confirmText: 'Verwerfen'
          }
        })
        const confirmed = await firstValueFrom(ref.afterClosed())
        if (!confirmed) return
      } finally {
        this.isClosing = false
      }
    }
    this.dialogRef.close()
  }

  async save(): Promise<void> {
    if (this.form.invalid || this.passwordMismatch) return

    this.isLoading = true
    this.errorMessage = null
    this.successMessage = null

    try {
      await this.clinicManagerService.changePassword(
        this.form.value.old_password,
        this.form.value.new_password
      )
      this.successMessage = 'Passwort wurde erfolgreich geändert.'
      this.form.reset()
      this.form.markAsPristine()
    } catch (error: any) {
      if (error?.status === 400 || error?.status === 401) {
        this.errorMessage = 'Das aktuelle Passwort ist nicht korrekt.'
      } else {
        this.errorMessage = 'Beim Ändern des Passworts ist ein Fehler aufgetreten.'
      }
    } finally {
      this.isLoading = false
    }
  }
}
